import {Injectable, OnDestroy} from '@angular/core';
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../store';
import {filter, first, map, switchMap} from "rxjs/operators";
import {Config} from "../../../../config/config";
import {TextUtils} from "../../../../shared/utils/text-utils";
import {MigrationDialogComponent} from "../../../../shared/components/dialogs/migration/migration-dialog.component";
import {DialogService} from "../../../../../ng2-bootstrap-modal/dialog.service";
import {RechargeComponent} from "../../../../shared/components/dialogs/recharge-dialog/recharge.component";
import {
  NotificationDialogComponent
} from "../../../../shared/components/dialogs/notification-dialog/notification-dialog.component";
import {PaymentDialogComponent} from "../../../../workspace/billing/payment-dialog/payment-dialog.component";
import {ActivatedRoute, Router} from "@angular/router";
import {combineLatest} from 'rxjs';
import {Subject} from "rxjs";
import {User} from "../../../../user/user";
import {UserSubscription, UserSubscriptionCategory} from "../../../../user/user-subscription";
import {Website} from "../../website";
import {clientRoles} from "../../../../user/user-roles-actions";
import {Workspace} from "../../../../user-managment/workspace";
import {GoogleAnalyticsService} from "../../../../shared/services/google-analytics/google-analytics.service";
import {DbModalService} from "../../../../modules/modal/modal";
import {CreateWebsiteService} from "../../../../services/local/create-website.service";
import {OneMonthFreeComponent} from "../../../../shared/components/dialogs/one-month-free/one-month-free.component";
import {Utils} from "../../../../shared/utils/utils";


@Injectable({
  providedIn: 'root'
})
export class MigrationService implements OnDestroy {

  private user: User;
  private websiteId: number;
  private userSubscription: UserSubscription;
  private workspace: Workspace;
  private hostedWebsitesCount: number;
  private hostedAndSpeedWebsites: number;
  private hostingLimit: number;
  private demoUser: boolean = false;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _store: Store<fromStore.DbStoreState>,
    private _router: Router,
    private _route: ActivatedRoute,
    private _dialogService: DialogService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _modalService: DbModalService,
    private _createWebsiteService: CreateWebsiteService,
  ) {
    this._getUserData();
    this._getCreatedWebsiteId();
  }

  private _getUserData(): void {
    combineLatest(
      this._store.select(fromStore.getUser),
      this._store.select(fromStore.getUserSubscription),
      this._store.select(fromStore.getCurrentWorkspace),
      this._store.select(fromStore.getWebsites)
    )
      .pipe(
        filter(([user, userSubscription, workspace]) => !!user && !!userSubscription && !!workspace)
      )
      .subscribe(([user, userSubscription, workspace, websites]) => {
        this.user = user;
        this.userSubscription = userSubscription;
        this.demoUser = Utils.isDemoUser(userSubscription, user);
        this.workspace = workspace;
        this.hostedWebsitesCount = websites.reduce((count: number, website: Website) => {
          if (!website.parentId && website.isActive && website.type === 'live' && !!website.domain_hash) {
            return count + 1;
          }
          return count;
        }, 0);
        this.hostedAndSpeedWebsites = websites.filter((website: Website) => !website.parentId && website.isActive && website.type === 'live' && (!!website.domain_hash || website.connectedFrom === 'speed_optimizer')).length;

        this.hostingLimit = this.user && this.user.hosting_services && this.userSubscription && this.userSubscription.plan ? parseInt(this.user.hosting_services.domain_count.parameter_value, 10) + this.user.additionalWebsiteCount : null;
      });

  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _getCreatedWebsiteId(): void {
    this._store.select(fromStore.getWebsitesLoaded)
      .pipe(
        first(loaded => loaded),
        switchMap(() => this._store.select(fromStore.getWebsites)),
        map(websites => websites && websites[0]),
        first(website => !!website)
      )
      .subscribe(website => {
        if (website) {
          this.websiteId = website.id;
        }
      });
  }

  public migrate(websiteId: number): void {
    if (this.userSubscription && this.userSubscription.trialBoosterClient) {
      return this._redirectToBoosterPro();
    }
    const canCreate = this.userSubscription && this.userSubscription.domainsPermission && this.userSubscription.domainsPermission.canCreateWebsite;
    if (this.user && !this.user.oneProductOrBundleClient && !canCreate) {
      return this._showExceededDialog();
    }
    this._store.select(fromStore.getWebsitePluginsLoaded(websiteId))
      .pipe(
        first(loaded => !!loaded, null),
        switchMap(() => this._store.select(fromStore.getWebsiteManagerPlugin(websiteId))),
        first()
      )
      .subscribe(plugin => {
        if (plugin && plugin.active) {
          this._store.select(fromStore.getWebsite(websiteId))
            .pipe(first())
            .subscribe(website => this._migrateWebsite(website));
        } else {
          this._downloadManagerPluginDialog();
        }
      });
  }

  private _downloadManagerPluginDialog(): void {
    this._dialogService.addDialog(MigrationDialogComponent);
  }

  private _showExceededDialog(): void {
    if (this.user && this.user.subscriptionId === Config.defaultStarterPlanId && this.demoUser) {
      return this._showCheckoutDialog();
    }
    if (this.workspace && this.workspace.roleId
      && ![clientRoles.SITE_DEV, clientRoles.COMPANY_DEV].includes(this.workspace.roleId)
      && this.userSubscription && this.userSubscription.recharge) {
      return this._showRechargeDialog();
    }
    let exceededDesc;
    let exceededBtnText = "text_upgrade";
    let exceededBtnColor = "green";
    let action = "upgrade";
    if (this.workspace.roleId && this.workspace.roleId === clientRoles.COMPANY_DEV) {
      action = "got it";
      exceededDesc = "text_domainsExceededContactOwner";
      exceededBtnText = "text_emailVerification.gotIt";
      exceededBtnColor = "blue";
    } else {
      switch (this.userSubscription.category) {
        case UserSubscriptionCategory.STARTER:
          exceededDesc = 'text_domainsExceededUpgradeToPaid';
          break;
        case UserSubscriptionCategory.PERSONAL:
        case UserSubscriptionCategory.PREMIUM:
          exceededDesc = 'text_hostedDomainsExceededDesc';
          break;
        case UserSubscriptionCategory.ENTERPRISE:
          exceededDesc = 'text_domainsExceededBuyAdditional';
          exceededBtnText = "text_buyNow";
          exceededBtnColor = 'blue';
          action = 'buyNow';
          break;
      }
    }

    this._dialogService.addDialog(NotificationDialogComponent, {
      header: TextUtils.getText('text_domainsExceededTitleDialog'),
      message: TextUtils.getText(exceededDesc),
      buttonText: TextUtils.getText(exceededBtnText),
      buttonColor: exceededBtnColor,
      action: action,
      hasCloseButton: true
    })
      .subscribe((confirmed: string): void => {
        if (confirmed) {
          if (confirmed === 'upgrade') {
            this._router.navigate(['/upgrade-plan']);
          } else if (confirmed === 'buyNow') {
            this._dialogService
              .addDialog(PaymentDialogComponent, {
                upgradeSubscription: this.userSubscription,
                fromDialog: true,
                showAdditional: true
              });
          }
        }
      });
  }

  private _showRechargeDialog(): void {
    const modal = this._modalService.open(RechargeComponent, {height: 208, width: 700});
    modal.componentInstance.userSubscription = this.userSubscription;
  }

  private _showCheckoutDialog(): void {
    const analyticsData = {
      eventCategory: 'Free Upgrade Offer',
      eventAction: "Locked button click",
      eventLabel: 'Migrate'
    };
    const modal = this._modalService.open(OneMonthFreeComponent, {height: 600, width: 940});
    modal.componentInstance.mode = 'creation';
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  private _migrateWebsite(website: Website): void {
    this._createWebsiteService.openMigrateWebsiteModal(website.id, website.site_url);
  }

  private _redirectToBoosterPro(): void {
    this._router.navigate([`/upgrade-plan`]);
}
}

